import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { setTokenProvider } from "@chess/shared";
import { configurePurchases } from "@/billing/purchases";
import { firebaseAuth } from "./firebase";

interface AuthCtx {
  ready: boolean;
  signedIn: boolean;
  uid: string | null;
  email: string | null;
  displayName: string | null;
  signInEmail: (email: string, password: string) => Promise<void>;
  signUpEmail: (email: string, password: string) => Promise<void>;
  signInGoogle: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState<boolean>(false);
  const [uid, setUid] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);

  useEffect(() => {
    try {
      setTokenProvider(() => firebaseAuth.getIdToken().catch(() => null));
      const unsub = firebaseAuth.subscribe((u) => {
        setUid(u?.uid ?? null);
        setEmail(u?.email ?? null);
        setDisplayName(u?.displayName ?? null);
        setReady(true);
        // RevenueCat app_user_id must be the Firebase UID; anonymous visitors
        // never configure the SDK.
        if (u?.uid) configurePurchases(u.uid);
      });
      return unsub;
    } catch (err) {
      // Firebase not configured — public pages still work; sign-in will surface
      // the same error when the user actually tries.
      console.warn("Auth not initialized:", err);
      setReady(true);
      return;
    }
  }, []);

  const signInEmail = useCallback(async (em: string, password: string) => {
    const u = await firebaseAuth.signInEmail(em, password);
    setEmail(u.email);
    setDisplayName(u.displayName);
  }, []);

  const signUpEmail = useCallback(async (em: string, password: string) => {
    const u = await firebaseAuth.signUpEmail(em, password);
    setEmail(u.email);
    setDisplayName(u.displayName);
  }, []);

  const signInGoogle = useCallback(async () => {
    const u = await firebaseAuth.signInGoogle();
    setEmail(u.email);
    setDisplayName(u.displayName);
  }, []);

  const sendPasswordReset = useCallback(async (em: string) => {
    await firebaseAuth.sendPasswordReset(em);
  }, []);

  const signOut = useCallback(async () => {
    await firebaseAuth.signOut();
    setEmail(null);
    setDisplayName(null);
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({
      ready,
      signedIn: !!email,
      uid,
      email,
      displayName,
      signInEmail,
      signUpEmail,
      signInGoogle,
      sendPasswordReset,
      signOut,
    }),
    [ready, uid, email, displayName, signInEmail, signUpEmail, signInGoogle, sendPasswordReset, signOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
